import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Send, Loader2, X, CheckCircle2, AlertCircle, History, BellPlus, Users } from 'lucide-react';
import { pushSupported, getSubscription, subscribeToPush } from '../../utils/push';

const API = process.env.REACT_APP_BACKEND_URL;
const LABEL_PRESETS = ['UPDATED', 'BREAKING', 'NEW', 'DEVELOPING', 'EXCLUSIVE', 'CATCH UP'];

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

const defaultBodyFor = (article) => {
  const raw = article.excerpt || article.subtitle || 'Fresh details on this story.';
  return raw.length > 240 ? `${raw.slice(0, 237)}…` : raw;
};

/**
 * Modal for (re-)broadcasting a push notification for a single published
 * article. Lets the admin override the tag, headline and body, previews the
 * exact wording subscribers will see, and shows the rolling delivery history
 * so they can tell if a broadcast actually landed.
 */
const PushArticleModal = ({ articleId, onClose, onSent }) => {
  const [article, setArticle] = useState(null);
  const [label, setLabel] = useState('UPDATED');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [deviceSubscribed, setDeviceSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const token = localStorage.getItem('admin_token');
  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/articles/${articleId}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const full = await res.json();
      setArticle(full);
      setLabel((full.last_push_label || 'UPDATED').toUpperCase());
      setTitle(full.title || '');
      setBody(defaultBodyFor(full));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  // Fetch push stats + check whether THIS device is already subscribed.
  const refreshStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/push/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch { /* non-fatal */ }
    try {
      if (pushSupported()) {
        const sub = await getSubscription();
        setDeviceSubscribed(!!sub);
      }
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  const enableOnThisDevice = async () => {
    setSubscribing(true);
    setError('');
    try {
      await subscribeToPush();
      setDeviceSubscribed(true);
      await refreshStats();
    } catch (e) {
      setError(e.message || 'Could not enable notifications on this device.');
    } finally {
      setSubscribing(false);
    }
  };

  const purgeAllSubscribers = async () => {
    if (!window.confirm(
      "PURGE every push subscriber?\n\n" +
      "Use this only when broadcasts are failing to deliver for every subscriber (usually because " +
      "VAPID keys have been rotated). Every device will have to opt in again to receive future pushes.\n\n" +
      "This cannot be undone.",
    )) return;
    setSubscribing(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/push/purge-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      window.alert(`Purged ${data.removed} subscription${data.removed === 1 ? '' : 's'}. Everyone must re-subscribe.`);
      setDeviceSubscribed(false);
      await refreshStats();
    } catch (e) {
      setError(e.message || 'Purge failed.');
    } finally {
      setSubscribing(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cleanLabel = (label || 'UPDATED').trim().toUpperCase().slice(0, 20) || 'UPDATED';
  const previewTitle = `${cleanLabel}: ${(title || article?.title || '').trim()}`;

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and body are both required.');
      return;
    }
    setSending(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/articles/${articleId}/push`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          label: cleanLabel,
          title: title.trim(),
          body: body.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
      // Re-fetch article so the history list refreshes with the new entry.
      await load();
      onSent?.(data);
    } catch (e) {
      setError(e.message || 'Push failed.');
    } finally {
      setSending(false);
    }
  };

  const history = [...((article && article.push_history) || [])].reverse();

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-6"
      onClick={onClose}
      data-testid="push-article-modal"
    >
      <div
        className="w-full max-w-2xl bg-[#0d0d0f] border border-red-900/50 rounded-lg shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-red-900/40 px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3 sticky top-0 bg-[#0d0d0f] rounded-t-lg z-10">
          <div className="h-9 w-9 rounded-lg bg-red-600 flex items-center justify-center shrink-0">
            <Bell className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black tracking-[0.3em] uppercase text-red-400">
              Broadcast push
            </p>
            <p className="text-sm text-white font-black truncate">
              {loading ? 'Loading…' : (article?.title || 'Article')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="push-modal-close"
            className="text-gray-400 hover:text-white p-1.5 rounded hover:bg-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
            Loading article…
          </div>
        ) : (
          <div className="p-4 sm:p-5 space-y-4">
            {/* Audience & this-device subscribe */}
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 shrink-0">
                <Users className="h-4 w-4 text-red-400" />
                <span className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                  Audience
                </span>
                <span
                  className="text-lg font-black text-white leading-none"
                  data-testid="push-modal-audience-count"
                >
                  {stats ? stats.subscribers.toLocaleString() : '-'}
                </span>
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                  subscriber{stats?.subscribers === 1 ? '' : 's'}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {deviceSubscribed ? (
                  <span
                    data-testid="push-modal-this-device-ok"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  >
                    <CheckCircle2 className="h-3 w-3" /> This device is subscribed
                  </span>
                ) : pushSupported() ? (
                  <button
                    type="button"
                    onClick={enableOnThisDevice}
                    disabled={subscribing}
                    data-testid="push-modal-subscribe-this-device"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[10px] font-black uppercase tracking-widest"
                  >
                    {subscribing ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellPlus className="h-3 w-3" />}
                    {subscribing ? 'Enabling…' : 'Enable on this device'}
                  </button>
                ) : (
                  <span className="text-[10px] text-yellow-400 tracking-widest uppercase">
                    Push not supported here
                  </span>
                )}
              </div>
              {stats && stats.subscribers === 0 && (
                <p className="w-full text-[11px] text-yellow-400/90 leading-snug pt-1 border-t border-zinc-800 mt-1">
                  No one has subscribed yet. Enable it on this device first so you can verify the ping actually lands on your phone.
                </p>
              )}
              {stats && stats.subscribers > 0 && (
                <button
                  type="button"
                  onClick={purgeAllSubscribers}
                  disabled={subscribing}
                  data-testid="push-modal-purge-all"
                  className="w-full text-[10px] font-black tracking-widest uppercase text-red-400 hover:text-red-300 mt-2 pt-2 border-t border-zinc-800 disabled:opacity-50"
                >
                  Purge all {stats.subscribers} subscriber{stats.subscribers === 1 ? '' : 's'} (VAPID reset)
                </button>
              )}
            </div>

            {/* Tag presets */}
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                Tag (prepended to headline)
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {LABEL_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setLabel(p)}
                    data-testid={`push-modal-preset-${p.toLowerCase()}`}
                    className={`px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest border transition-colors ${
                      cleanLabel === p
                        ? 'bg-red-600 border-red-500 text-white'
                        : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:text-white hover:border-zinc-700'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={20}
                data-testid="push-modal-label"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none font-black uppercase tracking-widest"
              />
            </div>

            {/* Title */}
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                Headline
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                data-testid="push-modal-title"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
              />
            </div>

            {/* Body */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                  Body
                </label>
                <button
                  type="button"
                  onClick={() => setBody(defaultBodyFor(article))}
                  className="text-[10px] text-gray-500 hover:text-red-300 tracking-widest uppercase"
                >
                  Reset to article excerpt
                </button>
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={240}
                rows={3}
                data-testid="push-modal-body"
                placeholder='e.g. "New evidence surfaced - witness statement changes the case."'
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none resize-none leading-relaxed"
              />
              <p className="text-[10px] text-gray-500 mt-1">{body.length} / 240 characters</p>
            </div>

            {/* Live preview */}
            <div className="rounded-lg border border-red-500/40 bg-gradient-to-br from-red-950/40 via-black to-zinc-950 p-3">
              <p className="text-[9px] font-black tracking-[0.3em] uppercase text-red-400 mb-2">
                What subscribers will see
              </p>
              <div className="rounded bg-black/40 border border-zinc-800 p-3">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded bg-red-600 flex items-center justify-center shrink-0">
                    <Bell className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      data-testid="push-modal-preview-title"
                      className="text-sm text-white font-black leading-tight break-words"
                    >
                      {previewTitle}
                    </p>
                    <p
                      data-testid="push-modal-preview-body"
                      className="text-xs text-gray-300 mt-1 leading-relaxed break-words"
                    >
                      {body || defaultBodyFor(article)}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1.5 truncate">
                      outlawmedia.com.au/stories/{article?.slug}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Send button */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={send}
                disabled={sending || !title.trim() || !body.trim()}
                data-testid="push-modal-send"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-black text-xs uppercase tracking-widest shadow-[0_0_18px_rgba(220,38,38,0.35)] transition-all"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? 'Broadcasting…' : 'Send push now'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-gray-300 hover:text-white font-black text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
            </div>

            {/* Result / error */}
            {result && (
              <div
                data-testid="push-modal-result"
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-emerald-950/40 border border-emerald-500/40 text-sm"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-black text-emerald-300 uppercase text-[11px] tracking-widest">
                    Broadcast complete
                  </p>
                  <p className="text-emerald-200/80 text-xs mt-0.5">
                    Delivered <strong className="text-white">{result.sent}</strong>
                    {' · '}Dead endpoints purged <strong className="text-white">{result.removed}</strong>
                    {result.reason ? ` · ${result.reason}` : ''}
                  </p>
                  {result.failures && Object.keys(result.failures).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-emerald-500/20">
                      <p className="text-[10px] text-yellow-300 font-black uppercase tracking-widest mb-1">
                        Push service errors
                      </p>
                      <ul className="text-[11px] text-yellow-200/80 space-y-0.5">
                        {Object.entries(result.failures).map(([code, count]) => (
                          <li key={code}>
                            <strong className="text-white">{count}×</strong>{' '}
                            {['401', '403', '404', '410', 'unknown'].includes(code) ? `HTTP ${code}` : 'HTTP error'}
                            {(code === '401' || code === '403') && ' - VAPID key mismatch (subscription un-pushable, now purged)'}
                            {code === '404' && ' - endpoint gone (purged)'}
                            {code === '410' && ' - subscription expired (purged)'}
                            {code === 'unknown' && ' - no HTTP status returned'}
                            {code === 'error' && ' - send raised an exception before reaching the push service'}
                          </li>
                        ))}
                      </ul>
                      {result.exception_samples && result.exception_samples.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-yellow-500/20">
                          <p className="text-[10px] text-red-300 font-black uppercase tracking-widest mb-1">
                            Exception detail
                          </p>
                          <ul className="text-[11px] text-red-200/90 space-y-1 font-mono">
                            {result.exception_samples.map((s, i) => (
                              <li key={i} className="break-words">{s}</li>
                            ))}
                          </ul>
                          <p className="text-[11px] text-yellow-200 mt-2 leading-snug">
                            Run <code className="text-red-300">GET /api/push/diagnose</code> (admin) or open
                            the Push tab &rarr; Diagnostics to see which VAPID env var is malformed.
                          </p>
                        </div>
                      )}
                      {result.sent === 0 && result.removed > 0 && (
                        <p className="text-[11px] text-yellow-200 mt-2 leading-snug">
                          Every subscription failed and was purged. If subscribers were previously working, your
                          VAPID_PUBLIC_KEY / VAPID_PRIVATE_PEM env vars have been changed since they subscribed -
                          they must opt in again on their device.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {error && (
              <div
                data-testid="push-modal-error"
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-950/40 border border-red-500/50 text-sm"
              >
                <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-300">{error}</p>
              </div>
            )}

            {/* History */}
            <div className="pt-4 border-t border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <History className="h-4 w-4 text-gray-500" />
                <p className="text-[10px] font-black tracking-[0.3em] uppercase text-gray-400">
                  Delivery history {history.length > 0 && `(last ${history.length})`}
                </p>
              </div>
              {history.length === 0 ? (
                <p className="text-xs text-gray-500 py-2" data-testid="push-modal-history-empty">
                  No pushes yet for this article. The next one will land here.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="push-modal-history">
                  {history.map((h, i) => (
                    <li
                      key={`${h.sent_at}-${i}`}
                      className="rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300">
                          {h.label || 'PUSH'}
                        </span>
                        <span className="text-white font-black truncate max-w-[220px]" title={h.title}>
                          {h.title}
                        </span>
                        <span className="ml-auto text-[10px] text-gray-500">{fmt(h.sent_at)}</span>
                      </div>
                      <p className="text-gray-400 mt-1 line-clamp-2 leading-snug">{h.body}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] uppercase tracking-widest">
                        <span className="text-emerald-400 font-black">
                          Delivered {h.sent ?? 0}
                        </span>
                        <span className={`font-black ${h.removed > 0 ? 'text-yellow-400' : 'text-gray-500'}`}>
                          Dead {h.removed ?? 0}
                        </span>
                        {h.reason && (
                          <span className="text-red-400 font-black">· {h.reason}</span>
                        )}
                        {h.by && <span className="text-gray-500 ml-auto">by {h.by}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushArticleModal;
